import React, { FC } from 'react'
import {
    Alert,
    Button,
    Modal,
    ModalBody as Body,
    ModalFooter as Footer,
    ModalHeader as Header,
    ModalTitle as Title,
} from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { ShareLock } from 'Hooks/useShareEditingTick'

/**
 * Two faces of one fact: somebody else is editing this share. The banner sits above a form that may be
 * read but not changed. The dialog interrupts, which it can afford to do — by the time it appears the user
 * has been away ten minutes and has nothing in progress to interrupt.
 *
 * Neither of them says who the other editor is, or whether it is the same person in another window:
 * there is nothing the reader could do differently either way.
 */

export interface LockNotice {
    /** The form may be read but not changed. */
    locked: boolean
    /** Worth interrupting for, because the share was ours a moment ago. */
    lost: boolean
}

/**
 * What a change of lease is worth saying. Losing one we were holding is the only case that interrupts: a
 * share that already belonged to somebody else when it opened says so in the banner, staying locked out
 * must not nag, and quietly taking a lease back after an idle spell is nobody's business.
 */
export const lockNotice = (previous: ShareLock | undefined, next: ShareLock): LockNotice => ({
    locked: 'mine' !== next,
    lost: 'mine' !== next && 'mine' === previous,
})

export const BimboShareLockBanner: FC<{show: boolean, lock: ShareLock}> = ({ show, lock }) => {
    const { t } = useTranslation()

    if (!show || 'mine' === lock) {
        return null
    }

    return (
        <Alert variant="secondary" className="bimbo-share-notice d-flex align-items-center mb-2">
            <i className="fa-solid fa-lock me-2"></i>
            {t('app.bimboShareForm.lock.readOnly')}
        </Alert>
    )
}

export const BimboShareInactivityModal: FC<{
    show: boolean
    lock: ShareLock
    onReload: () => void
}> = ({ show, lock, onReload }) => {
    const { t } = useTranslation()

    return (
        <Modal show={show} backdrop="static" keyboard={false} centered>
            <Header>
                <Title>{t('app.bimboShareForm.lock.title')}</Title>
            </Header>
            <Body>
                <p className="mb-0">
                    {t('mine' === lock
                        ? 'app.bimboShareForm.lock.changedWhileAway'
                        : 'app.bimboShareForm.lock.taken')}
                </p>
            </Body>
            <Footer>
                <Button variant="outline-primary" onClick={onReload}>
                    {t('app.bimboShareForm.liveNotice.reload')}
                </Button>
            </Footer>
        </Modal>
    )
}
